import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

/**
 * L'inventario — quello che porti nello zaino.
 *
 * ⚠️ MOCKUP. Nessuno di questi oggetti ha un backend: la lista è scritta qui
 * sotto, e possederli non dipende ancora da niente. Quando esisterà un
 * inventario vero si sostituisce `OGGETTI` e il resto regge.
 *
 * Tre per riga in quadrati col fondo più scuro del cuoio e il contorno cucito:
 * è la stessa cucitura delle card e della voce attiva nella barra, cioè il modo
 * in cui su questo materiale si delimita una zona. Il fondo più scuro fa da
 * ALLOGGIAMENTO — un oggetto appoggiato sul cuoio sembrerebbe caduto lì,
 * dentro una casella sembra riposto.
 *
 * Toccando un oggetto la griglia lascia il posto alla sua scheda, DENTRO la
 * stessa tasca invece di aprire un secondo pannello: lo zaino è già un
 * pannello aperto, e impilarne un altro sopra avrebbe messo due veli e due
 * gesti di chiusura sulla stessa cosa.
 */

interface Oggetto {
  id: string;
  /** Il file in `public/`. */
  img: string;
  /** Chiave i18n del nome. */
  nameKey: string;
  nameFallback: string;
  /**
   * La descrizione, quando l'oggetto ne ha una.
   *
   * Gli altri tre porteranno a una SEZIONE loro (mappa, evocazione,
   * portafoglio) che non è ancora costruita: fino ad allora la scheda dice che
   * sta arrivando invece di inventarsi una descrizione che poi andrebbe
   * smentita.
   */
  descKey?: string;
  descFallback?: string;
  /**
   * Quanti ne hai — solo per gli oggetti che si CONSUMANO.
   *
   * Mappa e Portafoglio non ce l'hanno perché non finiscono mai: stamparci
   * sopra un «×1» avrebbe suggerito che un giorno possa diventare zero, cioè
   * che si possano perdere. Il contatore non è decorazione, è l'unica cosa che
   * distingue le due famiglie a colpo d'occhio.
   *
   * ⚠️ Numeri di comodo: non esiste un inventario che li tenga.
   */
  qty?: number;
}

const OGGETTI: readonly Oggetto[] = [
  {
    id: 'map',
    img: '/map.png',
    nameKey: 'item.map',
    nameFallback: 'Map',
  },
  {
    id: 'summon-stone',
    img: '/avatar-summoner.png',
    nameKey: 'item.summon_stone',
    nameFallback: 'Summoning Stone',
    qty: 2,
  },
  {
    id: 'wallet',
    img: '/wallet.png',
    nameKey: 'item.wallet',
    nameFallback: 'Wallet',
  },
  {
    id: 'dragon-scale',
    img: '/squama-difesa.png',
    nameKey: 'item.dragon_scale',
    nameFallback: 'Dragon Scale',
    qty: 5,
    descKey: 'item.dragon_scale_desc',
    descFallback:
      'A scale from a legendary dragon: used in battle it grants you an extra life. Could it be the key to winning a tournament?',
  },
];

export function Inventory() {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const [apertoId, setApertoId] = useState<string | null>(null);
  /**
   * Si sta TORNANDO alla griglia, non ci si sta arrivando la prima volta.
   *
   * Serve a non far scivolare la griglia quando la tasca si apre: lì è già il
   * pannello intero a entrare, e due movimenti in versi diversi per un gesto
   * solo si leggono come un difetto.
   */
  const [tornato, setTornato] = useState(false);

  const aperto = OGGETTI.find((o) => o.id === apertoId) ?? null;

  const apri = (o: Oggetto) => {
    haptics.impact('light');
    setTornato(false);
    setApertoId(o.id);
  };

  const chiudi = () => {
    haptics.selection();
    setTornato(true);
    setApertoId(null);
  };

  // Entrare in un oggetto e tornarne fuori sono uno spostamento in PROFONDITÀ,
  // e usano lo stesso verso con cui l'app entra in un dettaglio: la scheda
  // arriva da destra, la griglia rientra da sinistra. Il `key` fa ripartire
  // l'animazione, che senza rimontaggio non avrebbe un fotogramma iniziale.
  if (aperto)
    return (
      <div key={aperto.id} className="screen-in-right">
        <SchedaOggetto oggetto={aperto} onBack={chiudi} />
      </div>
    );

  return (
    <div key="griglia" className={cn('grid grid-cols-3 gap-3 pb-2', tornato && 'screen-in-left')}>
      {OGGETTI.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => apri(o)}
          // Il conteggio sta nel nome accessibile e non nel contatore, che è
          // `aria-hidden`: letto da solo dopo il nome, quel numero sarebbe
          // stato «Squama di drago, 5» — un dato senza etichetta.
          aria-label={
            o.qty != null
              ? `${t(o.nameKey, { defaultValue: o.nameFallback })} ×${o.qty}`
              : undefined
          }
          // `gap-2.5` e non `gap-1.5`: il contatore sporge sotto la casella, e
          // col respiro stretto finiva appoggiato sulla riga del nome.
          className="flex flex-col items-center gap-2.5 transition-opacity active:opacity-70"
        >
          <Casella img={o.img} alt="" qty={o.qty} />
          <span className="w-full text-center text-[11px] font-bold leading-tight text-pack-fg">
            {t(o.nameKey, { defaultValue: o.nameFallback })}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * L'alloggiamento di un oggetto: quadrato, fondo scuro, contorno cucito.
 *
 * `card-edge` qui NON è la card teal dell'app: dentro `pack-scope` quella
 * classe è già ridefinita come cucitura tratteggiata (vedi globals.css), quindi
 * usarla è il modo di dire «stesso segno di tutto il resto dello zaino» invece
 * di riscrivere un tratteggio a mano che poi divergerebbe.
 */
function Casella({
  img,
  alt,
  qty,
  className,
}: {
  img: string;
  alt: string;
  qty?: number;
  className?: string;
}) {
  return (
    // `cn` e non una concatenazione: le classi passate da fuori devono poter
    // SOSTITUIRE quelle di base, e concatenandole restavano entrambe nella
    // lista — `w-full` batteva `w-28` e la scheda ingrandiva il disegno di
    // quattro volte, sgranandolo.
    <span
      className={cn(
        // `relative`: il contatore si appende all'angolo della casella.
        'card-edge relative flex aspect-square w-full items-center justify-center rounded-2xl bg-pack-deep p-2.5',
        className,
      )}
    >
      <img
        src={img}
        alt={alt}
        draggable={false}
        className="h-full w-full select-none object-contain"
      />

      {/* In basso a DESTRA e appeso al bordo, come su ogni inventario: è il
          punto dove non copre mai il disegno, che in tutte e quattro le
          illustrazioni è centrato e sale verso l'alto.
          Pieno e chiaro, non un contorno: è un dato che si legge di sfuggita
          mentre si scorre la griglia, e un numero cucito sul fondo scuro
          avrebbe avuto lo stesso peso visivo del bordo della casella. */}
      {qty != null && (
        <span
          aria-hidden
          className="absolute -bottom-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-pack-fg px-1 text-[11px] font-black leading-none tabular-nums text-pack-deep ring-2 ring-[var(--color-pack)]"
        >
          {qty}
        </span>
      )}
    </span>
  );
}

/**
 * La scheda di un oggetto.
 *
 * Il tasto per tornare porta il nome della TASCA, non una freccia sola: dentro
 * uno zaino aperto una freccia nuda non dice se si torna alla griglia o si
 * chiude tutto.
 */
function SchedaOggetto({ oggetto, onBack }: { oggetto: Oggetto; onBack: () => void }) {
  const { t } = useTranslation('general');
  const nome = t(oggetto.nameKey, { defaultValue: oggetto.nameFallback });

  return (
    <div className="flex flex-col gap-4 pb-2">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 self-start text-xs font-bold uppercase tracking-wide text-pack-muted transition-opacity active:opacity-60"
      >
        <ChevronLeft aria-hidden className="h-4 w-4" strokeWidth={3} />
        {t('inventory', { defaultValue: 'Inventory' })}
      </button>

      <div className="flex flex-col items-center gap-3 text-center">
        {/* `w-32` e non più grande: questi disegni sono PNG da un centinaio di
            pixel, e ingranditi oltre si sgranano. Meglio un oggetto nitido di
            uno grande e sfocato — la scheda deve farlo guardare, non zoomare. */}
        <Casella img={oggetto.img} alt="" qty={oggetto.qty} className="w-32 shrink-0 p-3" />

        <h2 className="text-xl font-black uppercase tracking-wide text-pack-fg">{nome}</h2>

        {/* Il conteggio scritto per esteso, oltre al contatore sulla casella:
            qui c'è lo spazio per dirlo a parole, e in una scheda che spiega
            l'oggetto «quanti ne hai» è parte della spiegazione. */}
        {oggetto.qty != null && (
          <p className="text-xs font-bold uppercase tracking-wide text-pack-muted">
            {t('item_qty', { defaultValue: 'You have {{n}}', n: oggetto.qty })}
          </p>
        )}

        <p className="max-w-[22rem] text-sm leading-relaxed text-pack-muted">
          {oggetto.descKey
            ? t(oggetto.descKey, { defaultValue: oggetto.descFallback })
            : t('item_soon', {
                defaultValue: 'This item opens a section of its own. Coming soon.',
              })}
        </p>
      </div>
    </div>
  );
}
